import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, ModalBody, ModalHeader } from "reactstrap";

import Loader from "@components/shared/Loader/Loader";
import WarningMessageBox from "@components/Unit/WarningMessageBox/WarningMessageBox";
import {
  CancelBookingFeasibilityData,
  CancelBookingProps,
} from "@models/components/user.model";
import handleResponse from "@services/apiConfig/api.response.service";
import API_SERVICES from "@services/index";
import commonUtils from "@utils/common.utils";

const CancelBooking: React.FC<CancelBookingProps> = ({
  bookingId,
  cancelModal,
  getUserBookings,
  toggleCancelModal,
}) => {
  const { t } = useTranslation();

  const [cancelFeasibility, setCancelFeasibility] = useState(
    {} as CancelBookingFeasibilityData
  );
  const [loader, setLoader] = useState(false);
  const [cancelLoader, setIsCancelLoader] = useState(false);

  useEffect(() => {
    if (cancelModal) {
      setLoader(true);
      checkCancelBookingFeasibility(bookingId);
    }
  }, [bookingId, cancelModal]);

  const cancelBooking = async (bookingId: string) => {
    setIsCancelLoader(true);
    const response = await API_SERVICES.BOOKING.CANCEL_BOOKING(bookingId);
    const responseData = handleResponse(response);
    setIsCancelLoader(false);
    if (responseData?.ok) {
      commonUtils?.toastService(t("bookingCancelled"));
      toggleCancelModal();
      getUserBookings();
    }
  };

  const checkCancelBookingFeasibility = async (bookingId: string) => {
    const response =
      await API_SERVICES.BOOKING.CHECK_CANCEL_FEASIBILITY(bookingId);
    const responseData = handleResponse(response);

    if (responseData?.ok) setCancelFeasibility(responseData?.data);
    setLoader(false);
  };

  const closeModal = () => {
    toggleCancelModal();
    setCancelFeasibility({} as CancelBookingFeasibilityData);
  };

  return (
    <Modal
      data-testid="booking-cancel-modal"
      isOpen={cancelModal}
      toggle={closeModal}
    >
      <Loader isLoading={loader || cancelLoader} />
      <ModalHeader toggle={closeModal}>{t("cancelBooking")}</ModalHeader>
      <ModalBody>
        <p>{t("cancelBookingConfirmationMsg")}</p>
        {!cancelFeasibility?.cancellable && cancelFeasibility?.message && (
          <WarningMessageBox message={cancelFeasibility?.message} />
        )}
        <div className="btn-grp mt-4 text-end">
          <button
            className="btn fg-btn fg-btn-grey"
            onClick={closeModal}
            data-testid="no-cancel-button"
          >
            {t("no")}
          </button>
          <button
            className="btn fg-btn fg-btn-lightblue ms-3"
            disabled={!cancelFeasibility?.cancellable || cancelLoader}
            onClick={() => cancelBooking(bookingId)}
            data-testid="cancel-button"
          >
            {t("yes")}
          </button>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default CancelBooking;
